import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaMobileAlt } from "react-icons/fa";
import {
    PaymentOtpChallenge,
    authorisePaymentOtp,
    checkCashApproval,
    requestCashApproval,
    resendPaymentOtp,
} from "../../services/api";

/**
 * The code that turns a cash booking into a sale.
 *
 * Until this is done the booking exists but nothing has been paid and no ticket
 * is issued. The point is accountability: a named person approved a named sale
 * for a named amount, so the amount is on screen before anything is typed.
 *
 * There is deliberately no way for the agent to let themselves past. If the code
 * will not arrive they can ask head office, who approve that one sale.
 */

const msgOf = (err: any, fallback: string) =>
    err?.response?.data?.errors?.[0]?.message ?? err?.message ?? fallback;

/** How often the agent's screen asks whether head office has approved yet. */
const APPROVAL_POLL_MS = 5000;

const CashOtpDialog: React.FC<{
    bookingId: string;
    challenge: PaymentOtpChallenge;
    onAuthorised: () => void;
    onCancel: () => void;
}> = ({ bookingId, challenge, onAuthorised, onCancel }) => {
    const [challengeId, setChallengeId] = useState(challenge.challengeId ?? "");
    const [sentTo, setSentTo] = useState(challenge.sentTo ?? "your registered number");
    const [amount] = useState(challenge.amount ?? "this booking");
    const [canEscalate, setCanEscalate] = useState(Boolean(challenge.canEscalate));
    const [secondsLeft, setSecondsLeft] = useState(challenge.resendInSeconds ?? 60);

    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    /** Set when retrying cannot help — the code is not the problem. */
    const [blocked, setBlocked] = useState("");

    const [askingHeadOffice, setAskingHeadOffice] = useState(false);
    const [reason, setReason] = useState("");
    const [waitingOnApproval, setWaitingOnApproval] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => { inputRef.current?.focus(); }, []);

    useEffect(() => {
        if (secondsLeft <= 0) return;
        const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [secondsLeft]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!code.trim() || busy) return;
        setBusy(true);
        setError("");
        try {
            const result = await authorisePaymentOtp(bookingId, challengeId, code.trim());
            if (result.ok) return onAuthorised();
            setError(result.message ?? "That code could not be checked.");
            setCode("");
            inputRef.current?.focus();
        } catch (err: any) {
            // A thrown error here is not a wrong code — it is something the
            // agent cannot fix by typing again, so it replaces the form rather
            // than sitting above it.
            setBlocked(msgOf(err, "This sale could not be approved."));
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        if (secondsLeft > 0 || busy) return;
        setBusy(true);
        setError("");
        try {
            const next = await resendPaymentOtp(bookingId, challengeId);
            if (next.challengeId) setChallengeId(next.challengeId);
            if (next.sentTo) setSentTo(next.sentTo);
            setSecondsLeft(next.resendInSeconds ?? 60);
            setCanEscalate(Boolean(next.canEscalate));
            setCode("");
            inputRef.current?.focus();
        } catch (err: any) {
            setError(msgOf(err, "Could not send another code."));
        } finally {
            setBusy(false);
        }
    };

    const askHeadOffice = async () => {
        if (busy) return;
        setBusy(true);
        setError("");
        try {
            await requestCashApproval(bookingId, reason.trim() || undefined);
            setAskingHeadOffice(false);
            setWaitingOnApproval(true);
        } catch (err: any) {
            setError(msgOf(err, "Could not ask head office."));
        } finally {
            setBusy(false);
        }
    };

    // While waiting, ask the server rather than making the agent refresh. The
    // customer is standing there; nobody should have to guess whether it landed.
    const poll = useCallback(async () => {
        try {
            const state = await checkCashApproval(bookingId);
            if (state.approved) { onAuthorised(); return true; }
            if (state.status === "declined") {
                setWaitingOnApproval(false);
                setBlocked("Head office did not approve this sale.");
                return true;
            }
        } catch {
            // A failed poll is not worth showing — the next one is 5 seconds away.
        }
        return false;
    }, [bookingId, onAuthorised]);

    useEffect(() => {
        if (!waitingOnApproval) return;
        let stop = false;
        const tick = async () => {
            if (stop) return;
            const done = await poll();
            if (!done && !stop) timer = setTimeout(tick, APPROVAL_POLL_MS);
        };
        let timer = setTimeout(tick, APPROVAL_POLL_MS);
        return () => { stop = true; clearTimeout(timer); };
    }, [waitingOnApproval, poll]);

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">

                {blocked ? (
                    <>
                        <h2 className="mb-2 text-center text-lg font-bold text-red-700">
                            This sale cannot go through
                        </h2>
                        <p className="mb-6 text-center text-sm text-gray-600">{blocked}</p>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="w-full rounded-lg bg-gray-100 py-2.5 font-semibold text-gray-700 transition hover:bg-gray-200"
                        >
                            Close
                        </button>
                    </>
                ) : waitingOnApproval ? (
                    <>
                        <h2 className="mb-2 text-center text-lg font-bold text-primary">
                            Waiting for head office
                        </h2>
                        <p className="mb-4 text-center text-sm text-gray-600">
                            They have been told about this {amount} sale. This screen will carry on
                            by itself as soon as it is approved.
                        </p>
                        <div className="mb-6 flex justify-center">
                            <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        </div>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="w-full text-center text-xs text-gray-500 underline"
                        >
                            Stop waiting and leave this booking unpaid
                        </button>
                    </>
                ) : askingHeadOffice ? (
                    <>
                        <h2 className="mb-2 text-center text-lg font-bold text-primary">
                            Ask head office to approve
                        </h2>
                        <p className="mb-4 text-center text-sm text-gray-500">
                            They will see this {amount} sale and can approve it for you.
                        </p>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            maxLength={300}
                            placeholder="What is wrong? e.g. no signal in the branch"
                            className="mb-4 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        {error && <div className="mb-3 text-center text-sm text-red-600">{error}</div>}
                        <button
                            type="button"
                            onClick={askHeadOffice}
                            disabled={busy}
                            className="w-full rounded-lg bg-primary py-2.5 font-semibold text-white transition hover:bg-blue-800 disabled:opacity-50"
                        >
                            {busy ? "Sending…" : "Send the request"}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setAskingHeadOffice(false); setError(""); }}
                            className="mt-3 w-full text-center text-xs text-gray-500 underline"
                        >
                            Back to the code
                        </button>
                    </>
                ) : (
                    <form onSubmit={submit}>
                        <div className="mb-4 flex justify-center">
                            <FaMobileAlt className="text-primary" size={28} />
                        </div>

                        <h2 className="mb-1 text-center text-lg font-bold text-primary">
                            Approve this sale
                        </h2>
                        <p className="mb-1 text-center text-2xl font-bold text-gray-800">{amount}</p>
                        <p className="mb-6 text-center text-sm text-gray-500">
                            Enter the code sent to <strong className="text-gray-700">{sentTo}</strong>.
                        </p>

                        <input
                            ref={inputRef}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={8}
                            placeholder="––––"
                            className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-3 text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-primary"
                        />

                        {error && <div className="mb-3 text-center text-sm text-red-600">{error}</div>}

                        <button
                            type="submit"
                            disabled={busy || !code.trim()}
                            className="w-full rounded-lg bg-primary py-2.5 font-semibold text-white transition hover:bg-blue-800 disabled:opacity-50"
                        >
                            {busy ? "Checking…" : "Approve and issue ticket"}
                        </button>

                        <div className="mt-4 text-center text-sm">
                            {secondsLeft > 0 ? (
                                <span className="text-gray-400">
                                    You can ask for another code in {secondsLeft}s
                                </span>
                            ) : (
                                <button type="button" onClick={resend} disabled={busy} className="font-medium text-primary">
                                    Send another code
                                </button>
                            )}
                        </div>

                        {canEscalate && (
                            <button
                                type="button"
                                onClick={() => { setAskingHeadOffice(true); setError(""); }}
                                className="mt-3 w-full text-center text-sm font-medium text-primary underline"
                            >
                                The code is not arriving — ask head office
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={onCancel}
                            className="mt-3 w-full text-center text-xs text-gray-500 underline"
                        >
                            Cancel — the booking will stay unpaid
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CashOtpDialog;
